/* ============================================================
   Soulskart — dialog stranding guard.

   This file owns one idea: NO DIALOG MAY LEAVE THE PAGE UNUSABLE.
   It holds three invariants, each of which shipped as a real freeze.

   ------------------------------------------------------------
   INVARIANT 1 — the coupon sheet may never outlive the cart drawer.

   #ss-coupon-modal is a <dialog> opened with showModal(). It lives in the
   FOOTER group, not inside the cart drawer, and the cart drawer is its own
   <dialog>. An open modal <dialog> puts the page in the top layer and makes
   the ENTIRE document inert.

   Applying a coupon re-renders the cart drawer underneath the sheet. Nothing
   closed the sheet when the drawer went away, so it stayed open in the top
   layer, sized to where the drawer used to be, and every tap on the page died
   until reload.

   ------------------------------------------------------------
   INVARIANT 2 — the body scroll lock may never outlive the last modal.

   THIS IS THE "SCREEN GOES STUCK AFTER APPLYING A COUPON" BUG. Reproduced
   locally against the real theme bundle, cart of 2 x ₹999 with welcome10:

     before apply   body.position=fixed   drawer open, modal   sheet open, modal
     after apply    body.position=fixed   NO dialog open at all

   assets/dialog.js showDialog() locks the page by writing
       body { position: fixed; width: 100%; top: -<scrollY>px }
   and closeDialog() is the ONLY code that ever undoes it.

   Applying a discount calls morphSection() on the header section, and the
   cart drawer <dialog> lives inside that section. The morph replaces the live
   dialog node, so the drawer stops being open — but closeDialog() never ran.
   The body stays position:fixed with nothing left to close. The page renders
   normally and cannot be scrolled at all. Only a reload clears it.

   Confirmed NOT specific to the coupon sheet: applying the same code straight
   from the drawer's own discount input strands the page identically. The
   coupon sheet is simply the path the storefront actually offers.

   ------------------------------------------------------------
   INVARIANT 3 — the cart drawer survives a discount update.

   Same root cause, softer symptom: the morph destroys the drawer, so applying
   a coupon appeared to close the shopper's cart. Since a lock left behind by a
   destroyed dialog is exactly what invariant 2 detects, the same check can put
   the drawer back rather than merely unlocking the page.

   ------------------------------------------------------------
   WHY body.style.position IS THE SIGNAL

   A clean close always clears the body styles BEFORE the dialog stops being
   open, because closeDialog() awaits its animation and then writes both. So
   "body is locked AND no modal is open" cannot happen on a real close — it
   only happens when a dialog was destroyed underneath the lock. That makes it
   a precise trigger rather than a heuristic, and it is why this does not need
   to know anything about discounts, morphs or section ids.

   The state must persist across two checks ~200ms apart before anything is
   undone, so a transient mid-open frame can never trip it.
   ============================================================ */
(function () {
  if (window.__ssCouponGuard) return;
  window.__ssCouponGuard = true;

  var CLOSERS = '.cart-drawer__close, [data-cart-drawer-close], [data-ss-coupon-close]';
  var REOPEN_WINDOW_MS = 6000;

  function sheet() { return document.getElementById('ss-coupon-modal'); }
  function drawerDialog() { return document.querySelector('.cart-drawer__dialog'); }
  function drawerComponent() { return document.querySelector('cart-drawer-component'); }

  function shut(el) {
    if (!el) return;
    try { if (typeof el.close === 'function' && el.open) el.close(); } catch (e) {}
    try { el.removeAttribute('open'); } catch (e) {}
  }

  /* :modal is the only reliable "is the document blocked" test. Supported in
     every browser this store sees; the fallback is here so an old engine
     degrades to doing nothing rather than throwing on every tick. */
  function anyModalOpen() {
    try {
      return !!document.querySelector('dialog:modal');
    } catch (e) {
      return !!document.querySelector('dialog[open]');
    }
  }

  function bodyLocked() {
    return document.body && document.body.style.position === 'fixed';
  }

  /* The inverse of what showDialog() wrote, including the scroll restore that
     closeDialog() would have done. top is stored as a negative offset. */
  function unlockBody() {
    var top = document.body.style.top || '0px';
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    var y = Math.abs(parseInt(top, 10)) || 0;
    try { window.scrollTo({ top: y, behavior: 'instant' }); }
    catch (e) { window.scrollTo(0, y); }
  }

  var strandedSince = 0;
  var sawDrawerAt = 0;

  function guard() {
    /* ---- invariant 1 ---------------------------------------------------- */
    var m = sheet();
    if (m && (m.open || m.hasAttribute('open'))) {
      if (!document.contains(m)) {
        shut(m);
      } else {
        var d = drawerDialog();
        if (!d || !d.open) shut(m);
      }
    }

    var live = drawerDialog();
    if (live && live.open) sawDrawerAt = Date.now();

    /* ---- invariants 2 and 3 --------------------------------------------- */
    if (anyModalOpen() || !bodyLocked()) { strandedSince = 0; return; }

    var now = Date.now();
    if (!strandedSince) { strandedSince = now; return; }
    if (now - strandedSince < 200) return;
    strandedSince = 0;

    /* The drawer was open moments ago and is not open now, while its lock is
       still on the body: it was destroyed, not closed. Put it back — the
       shopper was mid-cart and has just applied a discount they want to see.
       Unlock first so showDialog() re-locks against the right scroll offset. */
    var comp = drawerComponent();
    var dlg = drawerDialog();
    if (comp && dlg && typeof comp.showDialog === 'function' && (now - sawDrawerAt) < REOPEN_WINDOW_MS) {
      unlockBody();
      try { comp.showDialog(); return; } catch (e) {}
    }

    /* Nothing to restore, or restoring failed. Never leave the page locked. */
    unlockBody();
  }

  // The drawer's own close controls dismiss the sheet first.
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (!t.closest(CLOSERS)) return;
    var m = sheet();
    if (m && (m.open || m.hasAttribute('open'))) shut(m);
  }, true);

  // <dialog> open/close is an attribute change, so watch attributes.
  if ('MutationObserver' in window) {
    var raf;
    var mo = new MutationObserver(function () {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(guard);
    });
    mo.observe(document.documentElement, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['open']
    });
  }

  // Last resort. Cheap, and the cost of missing this is an unusable page.
  setInterval(guard, 300);

  window.addEventListener('pageshow', guard);
  document.addEventListener('DOMContentLoaded', guard);
})();
